import React from "react";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { endOfToday, startOfDay } from "date-fns/esm";
import { useIntl } from "react-intl";

import {
  Activity,
  AnyEntity,
  EntityType,
  EntityTypesSupportingRoutes,
} from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Button } from "@mapmycustomers/ui";

import { FULL_ADDRESS } from "@app/util/consts";
import { entityToAddressLines, formatRawDateRelative } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import styles from "./Stop.module.scss";

interface Props {
  activity: Activity;
  entityType: EntityTypesSupportingRoutes;
  onAdd?: (activity: Activity) => void;
  onEdit?: (payload: { entity: AnyEntity; entityType: EntityTypesSupportingRoutes }) => void;
  onRemove?: (activity: Activity) => void;
}

const Stop: React.FC<Props> = ({ activity, entityType, onAdd, onEdit, onRemove }) => {
  const intl = useIntl();
  const entity = entityType === EntityType.COMPANY ? activity.account : activity.contact;
  const { line1 = undefined, line2 = undefined } = entity
    ? entityToAddressLines(entity, FULL_ADDRESS, { locationWhenNoAddress: true })
    : {};

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <div className={styles.title}>
          {(entity ? entity?.name : activity.name) || (
            <i>
              {intl.formatMessage({
                id: "activities.calendarView.routeBuilder.steps.adjust.stop.title.noName",
                defaultMessage: "No name",
                description: "'No name' label when entity has no name",
              })}
            </i>
          )}
        </div>
        <div className={styles.subTitle}>
          {entity
            ? [line1, line2].filter(isDefined).join(", ")
            : formatRawDateRelative(
                startOfDay(parseApiDate(activity.startAt ?? "")).toISOString(),
                endOfToday(),
                "",
                "PPPPp"
              )}
        </div>
      </div>
      <div>
        {entity?.geoPoint ? (
          <Button
            className={styles.removeIcon}
            icon={<FontAwesomeIcon icon={faMinusCircle} />}
            iconOnly
            onClick={() => onRemove?.(activity)}
            type="link"
          />
        ) : entity ? (
          <div className={styles.noLocation}>
            <span className={styles.noLocationText}>
              {intl.formatMessage(
                {
                  id: "activities.calendarView.routeBuilder.steps.adjust.stop.editEntity.title",
                  defaultMessage:
                    "{entityType, select, accounts {Company} contacts {Person} other {Record}} associated but does not have valid address",
                  description:
                    "Unknown location warning in Route Builder when stop has no associated entity",
                },
                { entityType }
              )}
            </span>
            <Button
              className={styles.editEntityButton}
              onClick={() => onEdit?.({ entity, entityType })}
              type="link"
            >
              {intl.formatMessage(
                {
                  id: "activities.calendarView.routeBuilder.steps.adjust.stop.editEntity",
                  defaultMessage:
                    "Edit {entityType, select, accounts {Company} contacts {Person} other {Record}}",
                  description:
                    "Edit entity button label in Route Builder when stop has no associated entity",
                },
                { entityType }
              )}
            </Button>
          </div>
        ) : (
          <Button className={styles.addEntityButton} onClick={() => onAdd?.(activity)} type="link">
            {intl.formatMessage(
              {
                id: "activities.calendarView.routeBuilder.steps.adjust.stop.addEntity",
                defaultMessage:
                  "+ Add {entityType, select, accounts {Company} contacts {Person} other {Record}}",
                description:
                  "Add entity button label in Route Builder when stop has no associated entity",
              },
              { entityType }
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Stop;
